import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {CircularProgress, Paper, Box, Chip, TableContainer, TablePagination, Table, TableHead, TableSortLabel, TableRow, TableCell, TableBody, Button, Stack} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import { visuallyHidden } from '@mui/utils';
import { endpoint } from "./config";
import axios from "axios";
import { toast } from "react-toastify";
import { makeStyles } from '@mui/styles';


const useStyles = makeStyles({
  table: {
      //minWidth: 950,
  },
  margin:{
      marginLeft: '0.5rem',
      marginRight: '0.5rem',
  },
  toolbar:{
      padding: '10px',
      borderRadius: '8px',
      backgroundColor: '#E1F5FE',
  },
  loading: {
      color: '#2979ff',
      position: 'absolute',
      top: 'calc(50% - 45px)',
      left: 'calc(50% - 45px)',
      'z-index': 10,
  },    
  border:{
      height: '35px',
      borderLeft: '1px solid #bcbcbc8c',
      borderRight: '1px solid rgb(255 255 255)',
  },
  tableHead: {
      padding: '8px',
      '& > *': {
          padding: '8px',
      },
  }
});

const headCells = [
  {
    id: 'userName',
    numeric: false,
    disablePadding: false,
    label: 'User Name',
    width: "150px"
  },
  {
    id: 'emailId',
    numeric: false,
    disablePadding: false,
    label: 'Email ID',
  },
  {
    id: 'userType',
    numeric: false,
    disablePadding: false,
    label: 'User Type',
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'Status',
  },
  {
    id: 'designation',
    numeric: false,
    disablePadding: false,
    label: 'Designation',
  },
  {
    id: 'manager',
    numeric: false,
    disablePadding: false,
    label: 'Manager',
  },
  {
    id: 'billingStartDate',
    numeric: false,
    disablePadding: false,
    label: 'Billing Start Date',
  },
  {
    id: 'billingEndDate',
    numeric: false,
    disablePadding: false,
    label: 'Billing End Date',
  },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  //const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
  const { order, orderBy, onRequestSort } = props;
  
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            variant="head"
            sx={{whiteSpace: "nowrap", width: headCell.width}}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function Users() {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('userName');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [query, setQuery] = useState("");

  const navigate = useNavigate();
  const classes = useStyles();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClick = (event, rowId) => {
    const selectedIndex = selected.indexOf(rowId);
    console.log(selectedIndex);
    let newSelected = [];

    if (selectedIndex === -1) {
      //for multiselect rows
      //newSelected = newSelected.concat(selected, rowId);
      newSelected = [rowId];
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  useEffect(() => {
    //fetchUsers();

    const triggerCall = setTimeout(() => {
      fetchUsers();
    }, 500);

    return () => clearTimeout(triggerCall)

  }, [query]);



  const isSelected = (rowId) => selected.indexOf(rowId) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rowData.length) : 0;

  const visibleRows = useMemo(
    () =>
      stableSort(rowData, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [rowData, order, orderBy, page, rowsPerPage],
  );

  async function fetchUsers() {
    //const user = getUserInfo();
    setLoading(true);

    const response = await axios.get(
      `${endpoint}/users?query=${query}` 
    );
    const data = response.data;
    setLoading(false);
    //console.log(data);
    if (data.status) {
      const users = data.data;
      //console.log(users);
      //setEmployee(emp);
      
      setRowData(users);
      
    } else {
      toast("Please login with correct Employee Account.");
      console.log(data.message);
    }
  }
  const editUser = (row) =>{
    console.log(row);
    navigate(`/users/${row.userId}`);
  }

  return (
    <>
      <header className="page-title-bar flex flex-wrap justify-between align-center mb-3">
        <h1 className="page-title mr-sm-auto mb-0"> Users </h1>
        <Stack direction="row" alignItems={"center"} spacing={2}>
          <input type="text" className="form-control" name="search" placeholder="Search"
            value={query} onChange={(event) => setQuery(event.currentTarget.value)}></input>
          <Button size="small" sx={{"width": '180px'}} variant="contained" href="#/users/new" startIcon={<FontAwesomeIcon icon={"plus"} />}>Add New</Button>
        </Stack>
      </header>

      <Paper id="userGrid" style={{ width: "100%", minHeight: "300px" }}>
        {loading && <CircularProgress size={90} thickness={1} className={classes.loading} />}

        <TableContainer
          //sx={{ minWidth: 750, maxHeight: 440 }}
          sx={{ maxHeight: "70vh" }}
          size={"medium"}
          //style={{ height: "60vh", minHeight: "300px" }}
        >
          <Table aria-label="collapsible table" stickyHeader className={classes.table}>
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rowData.length}
              onSelectAllClick={()=>{}}
            />
            {/*
            <TableHead>
              <TableRow className={classes.tableHead}>
                <TableCell>User Name</TableCell>
                <TableCell>Email ID</TableCell>
                <TableCell>User Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Designation</TableCell>
                <TableCell>Manager</TableCell>
                <TableCell>Billing Start Date</TableCell>
                <TableCell>Billing End Date</TableCell>
              </TableRow>
            </TableHead>
            */
            }

            <TableBody>
              {visibleRows.map((row) => {
                const isItemSelected = isSelected(row.userId);
                return (
                  <TableRow hover key={row.userId} sx={{ cursor: "pointer" }} selected={isItemSelected} 
                      onClick={(event) => handleClick(event, row.userId)} onDoubleClick={()=>editUser(row)}>
                    <TableCell sx={{width: "250px"}}>{row.userName}</TableCell>
                    <TableCell>{row.emailId}</TableCell>
                    <TableCell>{row.userType}</TableCell>
                    <TableCell>
                      <Chip
                        variant="outlined"
                        color={row.status === 1 ? "primary" : "error"}
                        label={row.status === 1 ? "Active":"InActive"}
                      />
                    </TableCell>
                    <TableCell>{(row.employee && row.employee.designation) || ''}</TableCell>
                    <TableCell>{(row.employee && row.employee.manager) || ''}</TableCell>
                    <TableCell>{(row.employee && row.employee.billingStartDate) || ''}</TableCell>
                    <TableCell>{(row.employee && row.employee.billingEndDate) || ''}</TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={headCells.length} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rowData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );
}

export default Users;
